import {
  CampaignPerformanceDailyPoint,
  CampaignPerformanceDashboardResponse,
  CampaignPerformancePlatformTotals,
  PerformanceAudienceComparisonCell,
  PerformanceAudienceComparisonRow,
  PerformanceAudienceKpi,
  PerformanceAudienceStoreVisits,
  PerformanceAudienceSummary,
  PerformanceAudienceTrendPoint,
  PerformanceAudienceTrendSeries,
} from '@/services/endpoints/performanceAudience';
import { PerformanceDashboardViewModel } from '../types';
import { formatLocaleNumber, resolveIntlLocale } from './formatLocaleNumber';

export type MapCampaignPerformanceDashboardContext = {
  audienceName: string;
  googleAccountCount: number;
  metaAdAccountCount: number;
  locale?: string;
};

const EMPTY_TREND: PerformanceAudienceTrendSeries = {
  impressions: [],
  clicks: [],
  cost: [],
  ctr: [],
};

const getPlatform = (
  platforms: CampaignPerformancePlatformTotals[],
  key: 'google' | 'meta'
): CampaignPerformancePlatformTotals | undefined => platforms.find((p) => p.platform === key);

const parseIsoDate = (value: string): Date => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, (month ?? 1) - 1, day ?? 1);
};

const formatIsoDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const eachDayInclusive = (from: string, to: string): string[] => {
  const start = parseIsoDate(from);
  const end = parseIsoDate(to);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) {
    return [];
  }

  const days: string[] = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    days.push(formatIsoDate(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
};

const formatDayLabel = (date: string, locale: string): string => {
  const parsed = parseIsoDate(date);
  return new Intl.DateTimeFormat(resolveIntlLocale(locale), { weekday: 'short', day: 'numeric' }).format(parsed);
};

const formatCompact = (value: number, locale: string) => formatLocaleNumber(value, locale);

const formatCurrency = (value: number, currencyCode: string, locale: string) =>
  new Intl.NumberFormat(resolveIntlLocale(locale), {
    style: 'currency',
    currency: currencyCode || 'EUR',
    maximumFractionDigits: value % 1 === 0 ? 0 : 2,
  }).format(value);

const formatPercent = (value: number, locale: string) =>
  `${new Intl.NumberFormat(resolveIntlLocale(locale), { maximumFractionDigits: 2 }).format(value)}%`;

const formatDecimal = (value: number, locale: string) =>
  new Intl.NumberFormat(resolveIntlLocale(locale), { maximumFractionDigits: 2 }).format(value);

const unavailableCell = (): PerformanceAudienceComparisonCell => ({ value: '', unavailable: true });

const valueCell = (value: string): PerformanceAudienceComparisonCell => ({ value });

const resolveCurrencyCode = (google?: CampaignPerformancePlatformTotals, meta?: CampaignPerformancePlatformTotals) =>
  google?.currencyCode || meta?.currencyCode || 'EUR';

const buildTrend = (
  daily: CampaignPerformanceDailyPoint[],
  range: { from: string; to: string },
  locale: string
): PerformanceAudienceTrendSeries => {
  const byDate = new Map<string, { google: CampaignPerformanceDailyPoint | null; meta: CampaignPerformanceDailyPoint | null }>();

  for (const point of daily) {
    const entry = byDate.get(point.date) ?? { google: null, meta: null };
    if (point.platform === 'google') {
      entry.google = point;
    } else if (point.platform === 'meta') {
      entry.meta = point;
    }
    byDate.set(point.date, entry);
  }

  const dates = eachDayInclusive(range.from, range.to);
  const impressions: PerformanceAudienceTrendPoint[] = [];
  const clicks: PerformanceAudienceTrendPoint[] = [];
  const cost: PerformanceAudienceTrendPoint[] = [];
  const ctr: PerformanceAudienceTrendPoint[] = [];

  for (const date of dates) {
    const entry = byDate.get(date);
    const google = entry?.google;
    const meta = entry?.meta;
    const label = formatDayLabel(date, locale);

    const googleImpressions = google?.impressions ?? 0;
    const metaImpressions = meta?.impressions ?? 0;
    const googleClicks = google?.clicks ?? 0;
    const metaClicks = meta?.clicks ?? 0;

    impressions.push({ date, label, google: googleImpressions, meta: metaImpressions });
    clicks.push({ date, label, google: googleClicks, meta: metaClicks });
    cost.push({ date, label, google: google?.cost ?? 0, meta: meta?.cost ?? 0 });
    ctr.push({
      date,
      label,
      google: googleImpressions > 0 ? (googleClicks / googleImpressions) * 100 : 0,
      meta: metaImpressions > 0 ? (metaClicks / metaImpressions) * 100 : 0,
    });
  }

  return { impressions, clicks, cost, ctr };
};

const buildComparison = (
  google: CampaignPerformancePlatformTotals | undefined,
  meta: CampaignPerformancePlatformTotals | undefined,
  locale: string,
  currencyCode: string
): PerformanceAudienceComparisonRow[] => {
  const cellNumber = (value: number | null | undefined): PerformanceAudienceComparisonCell => {
    if (value == null) {
      return unavailableCell();
    }
    return valueCell(formatCompact(value, locale));
  };

  const cellCurrency = (value: number | null | undefined): PerformanceAudienceComparisonCell => {
    if (value == null) {
      return unavailableCell();
    }
    return valueCell(formatCurrency(value, currencyCode, locale));
  };

  const cellPercent = (value: number | null | undefined): PerformanceAudienceComparisonCell => {
    if (value == null) {
      return unavailableCell();
    }
    return valueCell(formatPercent(value, locale));
  };

  const cellDecimal = (value: number | null | undefined): PerformanceAudienceComparisonCell => {
    if (value == null) {
      return unavailableCell();
    }
    return valueCell(formatDecimal(value, locale));
  };

  return [
    { id: 'impressions', google: cellNumber(google?.impressions), meta: cellNumber(meta?.impressions) },
    { id: 'reach', google: cellNumber(google?.reach), meta: cellNumber(meta?.reach) },
    { id: 'frequency', google: cellDecimal(google?.frequency), meta: cellDecimal(meta?.frequency) },
    { id: 'clicks', google: cellNumber(google?.clicks), meta: cellNumber(meta?.clicks) },
    { id: 'ctr', google: cellPercent(google?.ctr), meta: cellPercent(meta?.ctr) },
    { id: 'cpc', google: cellCurrency(google?.cpc), meta: cellCurrency(meta?.cpc) },
    { id: 'totalCost', google: cellCurrency(google?.cost), meta: cellCurrency(meta?.cost) },
    { id: 'storeVisits', google: cellNumber(google?.storeVisits), meta: unavailableCell() },
  ];
};

const buildKpis = (
  google: CampaignPerformancePlatformTotals | undefined,
  meta: CampaignPerformancePlatformTotals | undefined,
  locale: string,
  currencyCode: string
): PerformanceAudienceKpi[] => {
  const googleImpressions = google?.impressions ?? 0;
  const metaImpressions = meta?.impressions ?? 0;
  const googleCost = google?.cost ?? 0;
  const metaCost = meta?.cost ?? 0;

  const reachValue = meta?.reach;
  const totalReach =
    reachValue == null ? { id: 'totalReach', value: '—', badge: 'meta_only' as const } : { id: 'totalReach', value: formatCompact(reachValue, locale), badge: 'meta_only' as const };

  return [
    { id: 'clusterSize', value: '—' },
    totalReach,
    {
      id: 'totalImpressions',
      value: formatCompact(googleImpressions + metaImpressions, locale),
      breakdown: {
        googleLabel: 'Google',
        googleValue: formatCompact(googleImpressions, locale),
        metaLabel: 'Meta',
        metaValue: formatCompact(metaImpressions, locale),
      },
    },
    {
      id: 'totalCost',
      value: formatCurrency(googleCost + metaCost, currencyCode, locale),
      breakdown: {
        googleLabel: 'Google',
        googleValue: formatCurrency(googleCost, currencyCode, locale),
        metaLabel: 'Meta',
        metaValue: formatCurrency(metaCost, currencyCode, locale),
      },
    },
  ];
};

const buildStoreVisits = (google: CampaignPerformancePlatformTotals | undefined): PerformanceAudienceStoreVisits | undefined => {
  if (google?.storeVisits == null) {
    return undefined;
  }

  return {
    total: google.storeVisits,
    locationCount: 0,
    status: 'active',
    locations: [],
  };
};

export const mapCampaignPerformanceDashboard = (
  response: CampaignPerformanceDashboardResponse | undefined,
  context: MapCampaignPerformanceDashboardContext
): PerformanceDashboardViewModel | undefined => {
  if (!response) {
    return undefined;
  }

  const locale = context.locale ?? 'it';
  const google = getPlatform(response.platforms ?? [], 'google');
  const meta = getPlatform(response.platforms ?? [], 'meta');
  const currencyCode = resolveCurrencyCode(google, meta);

  const summary: PerformanceAudienceSummary = {
    audienceName: context.audienceName,
    audienceUserCount: 0,
    googleAccountCount: context.googleAccountCount,
    metaAdAccountCount: context.metaAdAccountCount,
    googleCampaignCount: google?.campaignCount ?? 0,
    metaAdSetCount: meta?.adGroupCount ?? 0,
  };

  return {
    summary,
    kpis: buildKpis(google, meta, locale, currencyCode),
    trend: response.daily?.length || response.range ? buildTrend(response.daily ?? [], response.range, locale) : EMPTY_TREND,
    comparison: buildComparison(google, meta, locale, currencyCode),
    storeVisits: buildStoreVisits(google),
  };
};
